import { liveComparisons, navLabelOf } from './comparisons';
import { liveGuides } from './guides';
import { groupIndustries, liveIndustries, tradeName } from './live';
import { liveTemplates } from './templates';

/*
 * What the site footer links to: only pages that are published (routes.json), so a planned page is never linked. Industries are all shown (grouped
 * Outdoor / Cleaning / Home services, from the pages' own `group`); guides are capped (six) so the footer stays a navigation aid, with a link to the
 * hub that lists them all. Every live template is listed (no cap: the owner decided the last four get a footer link, and a cap equal to the count would silently drop the next
 * one); a template not named in FOOTER_ORDER follows the named ones. The footer test keeps the whole footer under 60 links. A new page appears here the moment it is listed in routes.json.
 */
export const FOOTER_GUIDES = 6;
export const HUB_TEMPLATES = '/templates';
export const HUB_GUIDES = '/resources';

/** Short names for the footer's lists (a page title is a headline, too long for a link). A page not named here falls back to its title without the brand. */
const LABELS: Record<string, string> = {
  '/templates/pressure-washing-quote-template': 'Pressure washing quote',
  '/templates/house-cleaning-quote-template': 'House cleaning quote',
  '/templates/landscaping-quote-template': 'Landscaping quote',
  '/templates/painting-estimate-template': 'Painting estimate',
  '/templates/fence-estimate-template': 'Fence estimate',
  '/templates/flooring-estimate-template': 'Flooring estimate',
  '/templates/cancellation-policy': 'Cancellation policy',
  '/templates/concrete-estimate-template': 'Concrete estimate',
  '/templates/tree-service-estimate-template': 'Tree removal estimate',
  '/templates/handyman-quote-template': 'Handyman quote',
  '/resources/how-to-price-pressure-washing': 'Pricing pressure washing',
  '/resources/how-to-price-house-cleaning': 'Pricing house cleaning',
  '/resources/how-to-price-landscaping': 'Pricing landscaping',
  '/resources/how-to-price-snow-removal': 'Pricing snow removal',
  '/resources/how-to-price-painting': 'Pricing painting',
  '/resources/how-to-price-a-fence': 'Pricing a fence',
};
/** These lead (the strongest keywords), in this order; any other page follows in the order the site lists them. Guides are cut at FOOTER_GUIDES (six). */
const FOOTER_ORDER = [
  '/templates/pressure-washing-quote-template',
  '/templates/house-cleaning-quote-template',
  '/templates/landscaping-quote-template',
  '/templates/painting-estimate-template',
  '/templates/fence-estimate-template',
  '/templates/flooring-estimate-template',
  '/templates/concrete-estimate-template',
  '/templates/tree-service-estimate-template',
  '/templates/handyman-quote-template',
  '/templates/cancellation-policy',
  '/resources/how-to-price-pressure-washing',
  '/resources/how-to-price-house-cleaning',
  '/resources/how-to-price-landscaping',
  '/resources/how-to-price-snow-removal',
  '/resources/how-to-price-painting',
  '/resources/how-to-price-a-fence',
];
export const footerOrder = <T extends { path: string }>(pages: T[]): T[] => {
  const rank = (p: T) => { const i = FOOTER_ORDER.indexOf(p.path); return i < 0 ? FOOTER_ORDER.length : i; };
  return pages.map((p, i) => ({ p, i })).sort((a, b) => rank(a.p) - rank(b.p) || a.i - b.i).map((x) => x.p);
};
export const footerLabel = (p: { path: string; title: string }): string => LABELS[p.path] ?? p.title.replace(/\s*\|\s*Scopie$/, '');

export interface FooterLink {
  href: string;
  label: string;
}

export function footerLinks() {
  return {
    industryGroups: groupIndustries(liveIndustries()).map((s) => ({ group: s.group, links: s.pages.map((p): FooterLink => ({ href: p.path, label: tradeName(p) })) })),
    templates: footerOrder(liveTemplates()).map((p): FooterLink => ({ href: p.path, label: footerLabel(p) })),
    guides: footerOrder(liveGuides()).slice(0, FOOTER_GUIDES).map((p): FooterLink => ({ href: p.path, label: footerLabel(p) })),
    comparisons: liveComparisons().map((p): FooterLink => ({ href: p.path, label: navLabelOf(p) })),
  };
}
